import {
  csatActionPreviewSchema,
  csatAnalyticsLookupsSchema,
  csatAnalyticsQuerySchema,
  csatAnalyticsSchema,
  csatPendingListSchema,
  csatSubmitResponseSchema,
  csatTicketSurveySchema,
  type CsatAnalyticsQuery,
  type SubmitCsatRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const csatPendingQueryKey = ['csat', 'pending'] as const;
export const csatLookupsQueryKey = ['csat', 'lookups'] as const;
export const csatTicketQueryKey = (ticketId: string) => ['csat', 'ticket', ticketId] as const;
export const csatAnalyticsQueryKey = (query: CsatAnalyticsQuery) =>
  ['csat', 'analytics', query] as const;
export const csatActionQueryKey = (token: string) => ['csat', 'actions', token] as const;

export function useCsatPending(enabled: boolean) {
  return useQuery({
    queryKey: csatPendingQueryKey,
    queryFn: () => apiFetch('/csat/pending', csatPendingListSchema),
    enabled,
  });
}

export function useCsatTicketSurvey(ticketId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: csatTicketQueryKey(ticketId ?? ''),
    queryFn: () => apiFetch(`/csat/ticket/${ticketId}`, csatTicketSurveySchema),
    enabled: Boolean(ticketId) && enabled,
  });
}

export function useCsatLookups(enabled: boolean) {
  return useQuery({
    queryKey: csatLookupsQueryKey,
    queryFn: () => apiFetch('/csat/lookups', csatAnalyticsLookupsSchema),
    enabled,
  });
}

export function useCsatAnalytics(query: CsatAnalyticsQuery, enabled: boolean) {
  const parsed = csatAnalyticsQuerySchema.parse(query);
  return useQuery({
    queryKey: csatAnalyticsQueryKey(parsed),
    queryFn: () =>
      apiFetch(
        `/csat/analytics${toSearchParams({
          page: parsed.page,
          pageSize: parsed.pageSize,
          from: parsed.from,
          to: parsed.to,
          departmentId: parsed.departmentId,
          teamId: parsed.teamId,
        })}`,
        csatAnalyticsSchema,
      ),
    enabled,
  });
}

export function useRespondCsat(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SubmitCsatRequest }) =>
      apiFetch(`/csat/${id}/respond`, csatSubmitResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: csatPendingQueryKey });
      await queryClient.invalidateQueries({ queryKey: csatTicketQueryKey(ticketId) });
      await queryClient.invalidateQueries({ queryKey: ['csat', 'analytics'] });
    },
  });
}

export function useCsatActionPreview(token: string | undefined) {
  return useQuery({
    queryKey: csatActionQueryKey(token ?? ''),
    queryFn: () => apiFetch(`/csat/actions/${token}`, csatActionPreviewSchema),
    enabled: Boolean(token),
  });
}

export function useSubmitCsatAction(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitCsatRequest) =>
      apiFetch(`/csat/actions/${token}`, csatSubmitResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: csatActionQueryKey(token) });
      await queryClient.invalidateQueries({ queryKey: csatPendingQueryKey });
    },
  });
}
